// Catalog.ID Client — Delegate API
// ============================================================================

import { api } from './client';
import type {
    ActivateDelegateRequest, ActivateDelegateResponse,
    CompleteDelegateRequest,
    UpdateRolesRequest,
    LinkDelegateRequest,
    AttestDelegateLinkRequest,
    DelegateLinkAttestation,
    UnlinkDelegateRequest,
    LinkedIdentity,
} from './types';

/** Sent to the organization's origin (`base`, '' = the entry node), where
 *  `begin` ran; anonymous — the invitee holds no session yet. */
export async function activateDelegate(req: ActivateDelegateRequest, base: string): Promise<ActivateDelegateResponse> {
    return api.post('/api/delegate/activate', req, { base, anonymous: true });
}

export async function completeOnboarding(delegateCid: string, req: CompleteDelegateRequest): Promise<void> {
    return api.post(`/api/delegate/${delegateCid}/complete`, req);
}

export async function updateRoles(orgCid: string, delegateCid: string, req: UpdateRolesRequest): Promise<void> {
    return api.put(`/api/organization/${orgCid}/delegates/${delegateCid}/roles`, req);
}

export async function freezeDelegate(delegateCid: string): Promise<void> {
    return api.post(`/api/delegate/${delegateCid}/freeze`);
}

export async function unfreezeDelegate(delegateCid: string): Promise<void> {
    return api.post(`/api/delegate/${delegateCid}/unfreeze`);
}

export interface DelegateRemovalResponse {
    rotationRequired?: boolean;
}

/** Remove a delegate from the organization (owner or admin). Irreversible, so a
 *  `delegate-remove` step-up signed with the REMOVER's own key rides along, and
 *  `terminateAgents` when the delegate is the principal of live agents
 *  (docs/cid-governance.md M15). */
export async function terminateDelegate(
    orgCid: string, delegateCid: string, req: { challengeSignature: string; terminateAgents?: boolean },
): Promise<DelegateRemovalResponse> {
    return api.del(`/api/organization/${orgCid}/delegates/${delegateCid}`, req);
}

/** Step 1 of a link: the DELEGATE's origin checks its password and signs an
 *  attestation for `personalCid`. Routed for the delegate (its origin, following
 *  `origin_only`), anonymously — no password goes anywhere else (D18, M15a). */
export async function attestDelegateLink(req: AttestDelegateLinkRequest): Promise<DelegateLinkAttestation> {
    const resp = await api.post<{ attestation: DelegateLinkAttestation }>(
        '/api/delegate/link/attest', req, { cid: req.delegateCid, anonymous: true });
    return resp.attestation;
}

/** Step 2 of a link: written at the personal account's origin, under its session. */
export async function linkDelegate(req: LinkDelegateRequest): Promise<void> {
    return api.post('/api/delegate/link', req);
}

/** Link a delegate identity to the active personal identity (docs/cid-governance.md
 *  M15a): the delegate's origin vouches for the password, then the personal origin
 *  writes the link — the two may be different nodes. */
export async function linkDelegateIdentity(
    req: { personalCid: string; delegateCid: string; delegateAuthHash: string },
): Promise<void> {
    const attestation = await attestDelegateLink({
        delegateCid: req.delegateCid, delegateAuthHash: req.delegateAuthHash, personalCid: req.personalCid,
    });
    await linkDelegate({ delegateCid: req.delegateCid, attestation });
}

export async function unlinkDelegate(req: UnlinkDelegateRequest): Promise<void> {
    return api.del('/api/delegate/link', req);
}

export async function getWrappedOrgKeys(): Promise<{ keyId: string; keyIndex: number; wrappedKey: string }[]> {
    return api.get('/api/delegate/wrappedkeys');
}

export async function getLinkedIdentities(): Promise<LinkedIdentity[]> {
    return api.get('/api/delegate/identities');
}
